/**
 * XaiPromptPanel — Explainable AI (XAI) Transparency Report
 *
 * Reusable component that renders a 3-layer explainability panel:
 *   Layer 1: Summary — plain-English "why" with feature importance bars
 *   Layer 2: Full Prompt — collapsible system prompt + user context + request
 *   Layer 3: Evidence — curated clinical/scientific citations
 *
 * Patent claims exercised: P-001 Claims 4,7,8,13,18,19 · P-003 Claims 1,4,5,11,14,16,19
 *
 * Props:
 *   promptData    — { system_prompt, user_message, model, model_tier, temperature, max_tokens }
 *   compact       — (bool) if true, renders summary-only inline (for chat messages)
 *   featureName   — (string) e.g. "Meal Plan Generator" for display context
 *   itemCitations — (array, optional) the sources for THIS result, as the backend resolves them
 *                   ({label, publisher, url}). Shown first, under "Sources for this result"; the
 *                   feature's standard citations follow. Apple 1.4.1, 2026-10-17.
 *   citationsOnly — (bool) for deterministic checks with no AI prompt (interaction rules,
 *                   allergen checks): render the Citations content alone, no prompt tabs.
 *   itemTitle     — (string, optional) what the sources are for, e.g. "St. John's wort + sertraline"
 */
import React, { useState, useMemo } from 'react';
import { buildXaiSummary, buildXaiEvidence, buildXaiClinical, normalizeItemCitations, normalizeItemWarnings } from '../utils/xaiHelpers';

export default function XaiPromptPanel({
  promptData, compact = false, featureName = 'AI Generation',
  itemCitations = null, citationsOnly = false, itemTitle = '',
  itemWarnings = null, itemGroupTitle = '',
}) {
  const summary = useMemo(() => buildXaiSummary(promptData), [promptData]);
  const evidence = useMemo(() => buildXaiEvidence(promptData, featureName), [promptData, featureName]);
  const clinical = useMemo(() => buildXaiClinical(promptData, featureName), [promptData, featureName]);
  // This result's own sources. Malformed entries are dropped inside normalizeItemCitations (and
  // logged), so one bad citation never blanks the panel. Empty -> the standard citations alone.
  const itemSources = useMemo(() => normalizeItemCitations(itemCitations), [itemCitations]);
  // G16 — the authorities' WARNINGS about this result, each with its own source. Shown FIRST.
  const warnings = useMemo(() => normalizeItemWarnings(itemWarnings), [itemWarnings]);

  // Apple 1.4.1: citations must be "easy for the user to find". Open ON the Citations tab
  // whenever there are citations to show — which, with baseline citations, is always.
  // EXCEPT on a domain rejection: that path renders Summary only (no Citations tab exists to
  // land on), so defaulting there would show an empty panel.
  const _domainRejected = promptData?.domain_validation?.result === 'REJECTED';
  const [activeLayer, setActiveLayer] = useState(
    (!_domainRejected && evidence.length > 0) ? 'evidence' : 'summary'
  );
  const [expandedSections, setExpandedSections] = useState({ system: true, context: true, request: false, params: false });

  // Shared Citations content: this result's sources first, then the standards it rests on.
  const citationsContent = (
    <div className="xai-layer-content" data-testid="xai-citations">
      {warnings.length > 0 && (
        <>
          <div className="xai-cite-group-title xai-cite-group-title--warn" data-testid="xai-warnings-title">
            ⚠️ Warnings from authorities{itemTitle ? `: ${itemTitle}` : ''}
          </div>
          {warnings.map((w, i) => (
            <div key={`warn-${i}`} className="xai-evidence-item xai-evidence-item--warning" data-testid="xai-warning">
              <div className="xai-evidence-desc">{w.text}</div>
              {w.citation && (
                <div className="xai-evidence-source">
                  {w.citation.url ? (
                    <a href={w.citation.url} target="_blank" rel="noopener noreferrer">{w.citation.title}</a>
                  ) : w.citation.title}
                  {w.citation.source ? ` — ${w.citation.source}` : ''}
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {itemSources.length > 0 && (
        <>
          <div className="xai-cite-group-title">
            {itemGroupTitle || 'Sources for this result'}{itemTitle ? `: ${itemTitle}` : ''}
          </div>
          {itemSources.map((item, i) => (
            <div key={`item-${i}`} className="xai-evidence-item xai-evidence-item--result">
              <div className="xai-evidence-title">{item.title}</div>
              {item.description && <div className="xai-evidence-desc">{item.description}</div>}
              <div className="xai-evidence-source">
                {item.url ? (
                  <a href={item.url} target="_blank" rel="noopener noreferrer">{item.source || item.url}</a>
                ) : item.source}
              </div>
              {item.note && <div className="xai-evidence-note" data-testid="xai-coi-note">{item.note}</div>}
            </div>
          ))}
        </>
      )}
      {itemSources.length > 0 ? (
        <div className="xai-cite-group-title">Standards this check is built on</div>
      ) : (
        <p className="xai-layer-intro">
          Sources for the health information and recommendations above. Each citation links to
          the publishing authority.
        </p>
      )}
      {evidence.map((item, i) => (
        <div key={i} className="xai-evidence-item">
          <div className="xai-evidence-title">{item.title}</div>
          <div className="xai-evidence-desc">{item.description}</div>
          <div className="xai-evidence-source">
            {item.url ? (
              <a href={item.url} target="_blank" rel="noopener noreferrer">{item.source}</a>
            ) : item.source}
          </div>
        </div>
      ))}
      {/* The former empty-state branch is gone. Baseline citations (xaiHelpers
          BASELINE_CITATIONS) are keyed to the OUTPUT rather than the user's profile, so every
          generation carries at least the standards it was built against. That empty state is
          what an App Store reviewer working from a sparse demo account saw under Apple
          Guideline 1.4.1, and it is now unreachable. */}
      <div className="xai-evidence-disclaimer">
        <strong>Disclaimer:</strong> Citations are provided for transparency. This is not
        medical advice. Always consult your healthcare provider before making medical
        decisions.
      </div>
    </div>
  );

  // Deterministic checks (no AI prompt): Citations alone, always available — even with no
  // per-result sources the feature's standard citations render, so it is never empty.
  if (citationsOnly) {
    return (
      <div className="xai-panel xai-panel--citations" onClick={(e) => e.stopPropagation()}>
        <div className="xai-panel-header">
          <div className="xai-panel-title">📚 Citations</div>
        </div>
        {citationsContent}
      </div>
    );
  }

  if (!promptData) return null;

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ── Compact mode (chat) ──
  // RETIRED 2026-10-01 for Apple 1.4.1. This was a SEPARATE render path that showed summary
  // factors and NO citations at all, so chat could never display a source under any
  // circumstance — an inconsistency a reviewer would reasonably treat as a gap. XAI is now one
  // widget with identical behaviour everywhere; the `compact` prop is accepted and ignored so
  // existing call sites keep working.
  void compact;

  // Domain validation rejection = summary-only (no prompt or evidence to show)
  const isDomainRejection = promptData.domain_validation?.result === 'REJECTED';

  // ── Full 3-layer panel ──
  return (
    <div className="xai-panel" onClick={(e) => e.stopPropagation()}>
      <div className="xai-panel-header">
        <div className="xai-panel-title">
          Citations &amp; AI Reasoning
          {!isDomainRejection && evidence.length > 0 && (
            <div className="xai-panel-subtitle">
              Sources for this health information are in the{' '}
              <button type="button" className="xai-inline-link"
                      onClick={() => setActiveLayer('evidence')}>
                Citations
              </button>{' '}tab.
            </div>
          )}
        </div>
        <div className="xai-panel-badge">XAI</div>
      </div>

      {/* Tabs — summary-only when domain rejected, full 3-layer otherwise */}
      <div className="xai-layers" onClick={(e) => e.stopPropagation()}>
        {/* Citations is the FIRST tab (Apple 1.4.1, 2026-10-17) — the tab a reviewer is looking
            for, named with the word their notice uses. Internal key stays 'evidence'. */}
        {!isDomainRejection && (
          <button
            className={`xai-layer-tab ${activeLayer === 'evidence' ? 'xai-layer-tab--active' : ''}`}
            onClick={() => setActiveLayer('evidence')}
          >
            Citations{(evidence.length + itemSources.length) > 0 ? ` (${evidence.length + itemSources.length})` : ''}
          </button>
        )}
        <button
          className={`xai-layer-tab ${activeLayer === 'summary' ? 'xai-layer-tab--active' : ''}`}
          onClick={() => setActiveLayer('summary')}
        >
          Summary
        </button>
        {!isDomainRejection && (
          <>
            <button
              className={`xai-layer-tab ${activeLayer === 'detail' ? 'xai-layer-tab--active' : ''}`}
              onClick={() => setActiveLayer('detail')}
            >
              Full Prompt
            </button>
            <button
              className={`xai-layer-tab ${activeLayer === 'clinical' ? 'xai-layer-tab--active' : ''}`}
              onClick={() => setActiveLayer('clinical')}
            >
              Clinical
            </button>
          </>
        )}
      </div>

      {/* ── Layer 1: Summary ── */}
      {activeLayer === 'summary' && (
        <div className="xai-layer-content">
          {/* Domain validation pre-step (if present) */}
          {summary.domainValidation && (
            <div className="xai-domain-validation">
              <div className="xai-summary-item">
                <div className="xai-summary-icon">{summary.domainValidation.result === 'VALID' ? '\u2705' : '\u274C'}</div>
                <div className="xai-summary-body">
                  <div className="xai-summary-label">Domain Validation</div>
                  <div className="xai-summary-value">
                    {summary.domainValidation.result === 'REJECTED'
                      ? `Query rejected — not related to ${summary.domainValidation.domain} (${summary.domainValidation.entity_type})`
                      : `Input validated as ${summary.domainValidation.domain}-related (${summary.domainValidation.entity_type})`
                    }
                  </div>
                  {summary.domainValidation.reason && (
                    <div className="xai-summary-value" style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                      {summary.domainValidation.reason}
                    </div>
                  )}
                  <div className="xai-importance">
                    <div className="xai-importance-bar">
                      <div className="xai-importance-fill" style={{ width: '100%' }}></div>
                    </div>
                    <span className="xai-importance-pct">Pre-check</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isDomainRejection ? (
            <p className="xai-layer-intro">This query was rejected by the domain validation pre-check before reaching the AI model.</p>
          ) : (
            <>
              <p className="xai-layer-intro">Here's why the AI made these specific recommendations for you:</p>

              {summary.factors.map((factor, i) => (
                <div key={i} className="xai-summary-item">
                  <div className="xai-summary-icon">{factor.icon}</div>
                  <div className="xai-summary-body">
                    <div className="xai-summary-label">{factor.label}</div>
                    <div className="xai-summary-value">{factor.value}</div>
                    {factor.importance > 0 && (
                      <div className="xai-importance">
                        <div className="xai-importance-bar">
                          <div className="xai-importance-fill" style={{ width: `${factor.importance}%` }}></div>
                        </div>
                        <span className="xai-importance-pct">
                          {factor.importance >= 100 ? 'CRITICAL' : `${factor.importance}% influence`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="xai-confidence-box">
                <span className="xai-confidence">
                  <span className="xai-confidence-dot"></span>
                  {summary.confidenceLabel}
                </span>
                <span className="xai-confidence-detail">
                  AI had context from {summary.contextSourceCount} data source{summary.contextSourceCount !== 1 ? 's' : ''} including {summary.contextSources.slice(0, 3).join(', ')}{summary.contextSources.length > 3 ? `, and ${summary.contextSources.length - 3} more` : ''}.
                </span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Layer 2: Full Prompt ── */}
      {activeLayer === 'detail' && (
        <div className="xai-layer-content">

          <div className="xai-prompt-section">
            <div
              className={`xai-prompt-section-header ${expandedSections.system ? 'xai-prompt-section-header--open' : ''}`}
              onClick={() => toggleSection('system')}
            >
              <span className="xai-arrow">{expandedSections.system ? '\u25BC' : '\u25B6'}</span>
              System Instructions
            </div>
            {expandedSections.system && (
              <div className="xai-prompt-text">{promptData.system_prompt}</div>
            )}
          </div>

          {/* #101: the clinical context block. `expandedSections.context` has existed since the
              panel was written but NOTHING rendered it — when #57 moved the UHC out of the system
              prompt for Bedrock prompt caching, this section was never added, so the tab claiming to
              show "the full prompt" silently omitted every clinical fact in it. Rendered only when
              the surface supplies it (the meal-plan path folds its context into system_prompt). */}
          {promptData.user_health_context && (
            <div className="xai-prompt-section">
              <div
                className={`xai-prompt-section-header ${expandedSections.context ? 'xai-prompt-section-header--open' : ''}`}
                onClick={() => toggleSection('context')}
              >
                <span className="xai-arrow">{expandedSections.context ? '▼' : '▶'}</span>
                Your Health Context
              </div>
              {expandedSections.context && (
                <div className="xai-prompt-text">{promptData.user_health_context}</div>
              )}
            </div>
          )}

          <div className="xai-prompt-section">
            <div
              className={`xai-prompt-section-header ${expandedSections.request ? 'xai-prompt-section-header--open' : ''}`}
              onClick={() => toggleSection('request')}
            >
              <span className="xai-arrow">{expandedSections.request ? '\u25BC' : '\u25B6'}</span>
              Your Request
            </div>
            {expandedSections.request && (
              <div className="xai-prompt-text">{promptData.user_message}</div>
            )}
          </div>

          <div className="xai-prompt-section">
            <div
              className={`xai-prompt-section-header ${expandedSections.params ? 'xai-prompt-section-header--open' : ''}`}
              onClick={() => toggleSection('params')}
            >
              <span className="xai-arrow">{expandedSections.params ? '\u25BC' : '\u25B6'}</span>
              Model Parameters
            </div>
            {expandedSections.params && (
              <div className="xai-params">
                <div className="xai-param">Model: <span>{promptData.model}</span></div>
                <div className="xai-param">Temperature: <span>{promptData.temperature}</span></div>
                <div className="xai-param">Max Tokens: <span>{promptData.max_tokens?.toLocaleString()}</span></div>
                <div className="xai-param">Provider: <span>AWS Bedrock (HIPAA)</span></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Layer 3: Evidence (Citations) ── */}
      {activeLayer === 'evidence' && citationsContent}

      {/* ── Layer 4: Clinical (provider-grade) ── */}
      {activeLayer === 'clinical' && (
        <div className="xai-layer-content">
          <p className="xai-layer-intro">Provider-grade clinical view of the reasoning:</p>

          <div className="xai-evidence-item">
            <div className="xai-evidence-title">Clinical assessment</div>
            <div className="xai-evidence-desc">{clinical.assessment}</div>
            <div className="xai-evidence-source">Data sources: {clinical.sourceCount} &middot; Confidence: {clinical.confidenceLabel}</div>
          </div>

          <div className="xai-evidence-item">
            <div className="xai-evidence-title">Confidence interval</div>
            <div className="xai-evidence-desc">
              {clinical.confidenceInterval.lowerPct}%&ndash;{clinical.confidenceInterval.upperPct}% (center {clinical.confidenceInterval.centerPct}%)
            </div>
            <div className="xai-evidence-source">Interval tightens as corroborating data sources increase.</div>
          </div>

          {clinical.counterfactuals.length > 0 && (
            <>
              <p className="xai-layer-intro">Counterfactuals (what would change the recommendation):</p>
              {clinical.counterfactuals.map((c, i) => (
                <div key={i} className="xai-evidence-item">
                  <div className="xai-evidence-title">{c.condition}</div>
                  <div className="xai-evidence-desc">{c.effect}</div>
                </div>
              ))}
            </>
          )}

          {clinical.clinicalInputs.length > 0 && (
            <>
              <p className="xai-layer-intro">Weighted clinical inputs (by contribution):</p>
              {clinical.clinicalInputs.map((f, i) => (
                <div key={i} className="xai-evidence-item">
                  <div className="xai-evidence-title">{f.label} &mdash; {f.weightPct}%</div>
                  {f.detail && <div className="xai-evidence-desc">{f.detail}</div>}
                </div>
              ))}
            </>
          )}

          {/* The citation list used to be re-rendered here as well. Removed 2026-10-01: sources
              now live in exactly ONE place so a reader (or an App Store reviewer) has a single
              unambiguous destination. The two tabs now split cleanly —
              Citations = external authority · Clinical = what THIS user's data contributed. */}
          {clinical.citationCount > 0 && (
            <div className="xai-evidence-item">
              <div className="xai-evidence-title">Evidence base</div>
              <div className="xai-evidence-desc">
                {clinical.citationCount} source{clinical.citationCount === 1 ? '' : 's'} support
                this recommendation.
              </div>
              <div className="xai-evidence-source">
                <button type="button" className="xai-inline-link"
                        onClick={() => setActiveLayer('evidence')}>
                  View citations
                </button>
              </div>
            </div>
          )}

          <div className="xai-evidence-disclaimer">
            <strong>Clinical note:</strong> Provider-oriented summary for informational review. Not a substitute for professional clinical judgment.
          </div>
        </div>
      )}
    </div>
  );
}
